import { ClientId, IMessage, BrokerId } from 'magpie-types';
import { Logger } from 'simple-logging-lib';
import { CROSS_TAB_LINK_LOGGER_NAME } from '../../config/constants';
import { CrossTabLinkError } from '../../errors';
import { Observable } from 'reactive-lib/observable/observable';
import { Subscription } from 'reactive-lib/observable/subscription';
import { Subject } from 'reactive-lib/observable/subject';
import { getWindow } from '../../utils';
import { LinkOperation, linkOperations } from './operations';
import { MessageLink } from '../../models';

const BROADCAST_KEY_PREFIX = '@@magpie/cross-tab-link';

interface CrossTabLinkOptions {
  messageLink: MessageLink;
  brokerId: BrokerId;
}

export class CrossTabLink {
  static #instance: CrossTabLink;
  #events = new Subject<LinkOperation>();
  #subscription?: Subscription;
  #messageLinkSubscription?: Subscription;
  logger = Logger.getLogger(CROSS_TAB_LINK_LOGGER_NAME);
  options: CrossTabLinkOptions | null = null;
  private constructor() {}

  static getInstance(): CrossTabLink {
    if (!CrossTabLink.#instance) {
      CrossTabLink.#instance = new CrossTabLink();
    }
    return CrossTabLink.#instance;
  }

  getBroadcastKey(clientId: ClientId): string {
    return `${BROADCAST_KEY_PREFIX}/${clientId}`;
  }

  private broadcast = (operation: LinkOperation, clientId: ClientId) => {
    const broadcastKey = this.getBroadcastKey(clientId);
    try {
      getWindow().localStorage.setItem(broadcastKey, JSON.stringify(operation));
      getWindow().localStorage.removeItem(broadcastKey);
    } catch (e) {
      throw new CrossTabLinkError(CrossTabLinkError.Reason.LOCAL_STORAGE_ERROR);
    }
  };

  connect = (options: CrossTabLinkOptions): Observable<LinkOperation> => {
    this.options = options;
    getWindow().addEventListener('storage', this.onStorageEvent);
    const events$ = this.#events.asObservable();
    this.#subscription = events$.subscribe(this.onLinkOperation);
    this.#messageLinkSubscription = this.getOptions()
      .messageLink.asObservable()
      .subscribe(this.onMessage);
    return events$;
  };

  disconnect = () => {
    getWindow().removeEventListener('storage', this.onStorageEvent);
    this.#events.complete();
    this.#subscription?.unsubscribe();
    this.#messageLinkSubscription?.unsubscribe();
  };

  getOptions = (): CrossTabLinkOptions => {
    if (!this.options) {
      throw new CrossTabLinkError(CrossTabLinkError.Reason.NOT_CONNECTED);
    }
    return this.options;
  };

  onStorageEvent = ({ key, newValue }: StorageEvent) => {
    if (key?.startsWith(BROADCAST_KEY_PREFIX) && newValue) {
      this.logger.debug('X-Tab Link Storage Event', key, newValue);
      try {
        this.#events.next(JSON.parse(newValue));
      } catch (e) {
        this.logger.error('Failed to parse storage event', e);
      }
    }
  };

  onMessage = (message: IMessage) => {
    if (message.senderBrokerId !== this.getOptions().brokerId) {
      return;
    }
    if (message.allowCrossTab) {
      this.logger.debug('Broadcasting message via x-tab link', message);
      this.broadcast(linkOperations.sendMessage(message), message.sender);
    }
  };

  onLinkOperation = (operation: LinkOperation) => {
    this.logger.info('Received x-tab link operation', operation);
    if (linkOperations.sendMessage.match(operation)) {
      this.getOptions().messageLink.next(operation.payload);
    }
  };
}
